/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { platformRequestOption } from 'hooks/PlatformContext';
import { IApiRequest } from 'interfaces/apiRequestInterface';
import {
  getTokenFromLocalStorage,
  setTokenToLocalStorage,
} from 'utils/requestResponse';

let sessionExpiredCallback: (() => void) | null = null;
let lastSessionRestoreTime = 0;

export const notifySessionRestored = () => {
  lastSessionRestoreTime = Date.now();
};

export const setSessionExpiredCallback = (callback: () => void) => {
  sessionExpiredCallback = callback;
};

export const triggerSessionExpired = () => {
  handleSessionExpired();
};

const handleSessionExpired = (requestUrl?: string) => {
  // Never trigger session expired for authentication endpoints or status queries
  if (
    requestUrl &&
    [
      'CheckUserCredential',
      'GetUserLoginStatus',
      'RefreshToken',
      'GetSupportedLanguages',
    ].some((ep) => requestUrl.includes(ep))
  ) {
    return;
  }

  // Grace period of 10s after session restore to prevent stale in-flight responses from re-locking
  if (Date.now() - lastSessionRestoreTime < 10000) {
    console.warn(
      'Session expired ignored due to recent session restore grace period',
    );
    return;
  }

  if (sessionExpiredCallback) {
    sessionExpiredCallback();
  } else {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRefreshToken');
    window.location.href = '/login';
  }
};

const apiRequest = async ({
  url,
  payload,
  method,
  responseType,
}: IApiRequest) => {
  const token = localStorage.getItem('userToken');
  const refreshToken = localStorage.getItem('userRefreshToken');

  const requestHeader = platformRequestOption.requestHeader({
    token: token,
  });

  const requestData = {
    ...platformRequestOption.requestData,
    ...(payload as { [key: string]: unknown }),
  };

  const postRequestOptions = {
    method: method,
    headers: requestHeader,
    data: JSON.stringify(requestData),
    url: `${platformRequestOption.url}/${url}`,
  };

  const fetchRequestOptions = {
    method: method,
    headers: requestHeader,
    url: `${platformRequestOption.url}/${url}`,
  };

  const config = ['POST', 'PUT', 'DELETE'].includes(method)
    ? postRequestOptions
    : fetchRequestOptions;

  const requestRefreshToken = {
    ...platformRequestOption.requestData,
    ...({ RefreshToken: refreshToken } as { [key: string]: unknown }),
  };

  const refreshTokenOptions = {
    method: 'POST',
    headers: requestHeader,
    data: JSON.stringify(requestRefreshToken),
    url: `${platformRequestOption.url}/RefreshToken`,
  };

  try {
    const response = await axios.request({ ...config, responseType });
    return response;
  } catch (error: any) {
    if (error.response) {
      const errorData = error.response.data?.[0];

      if (
        errorData?.TokenVerified === 'FALSE' ||
        errorData?.Error === '2031'
      ) {
        handleSessionExpired(url);
        throw error;
      }

      if (
        errorData?.UserLoginCorrect === 'FALSE' &&
        errorData?.UserMustBeLoggedOut === 'Yes' &&
        errorData?.Error === '2041'
      ) {
        handleSessionExpired(url);
        throw error;
      }

      if (
        errorData?.Error === '2034' ||
        errorData?.status === '401'
      ) {
        if (token && refreshToken) {
          try {
            const response = await axios.request(refreshTokenOptions);
            const { LiveToken, RefreshToken } = response.data[0];

            setTokenToLocalStorage('userToken', LiveToken);
            setTokenToLocalStorage('userRefreshToken', RefreshToken);

            const newLiveToken =
              getTokenFromLocalStorage('userToken');
            const newRequestHeader =
              platformRequestOption.requestHeader({
                token: newLiveToken,
              });

            const newConfig = {
              ...config,
              headers: newRequestHeader,
            };

            if (LiveToken) {
              const previousFailedRequest =
                await axios.request(newConfig);
              return previousFailedRequest;
            }
          } catch (refreshError) {
            handleSessionExpired(url);
            throw refreshError;
          }
        } else {
          handleSessionExpired(url);
          throw error;
        }
      }
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export default apiRequest;
